import { useState } from "react";
import { Link } from "react-router-dom";

import { api } from "@/lib/api";
import { googleAuthUrl } from "@/lib/googleAuth";
import { setStoredToken } from "@/types";
import type { AuthUser } from "@/types";
import { GoogleSignInButton } from "@/components/shared/common/GoogleSignInButton";

interface SignupPageProps {
  onSignup: (user: AuthUser, token: string) => void;
}

export function SignupPage({ onSignup }: SignupPageProps) {
  const [displayName, setDisplayName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) {
      setError("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      const { token, user } = await api.signup({ display_name: displayName, email, password });
      setStoredToken(token);
      onSignup(user, token);
    } catch (err: any) {
      setError(err.message || "Signup failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-secondary flex flex-col">
      <div className="flex flex-1 items-center justify-center px-4 py-12 relative">
        <Link to="/" className="absolute top-6 left-6 flex items-center justify-center h-9 w-9 rounded-full bg-white shadow-card text-text-secondary hover:text-near-black transition-colors" title="Back to home">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
            </svg>
          </Link>

          <div className="w-full max-w-md">
          <div className="rounded-card bg-white p-8 shadow-card">
            <div className="mb-8 text-center">
              <h1 className="text-[28px] font-bold text-near-black">Create your account</h1>
              <p className="mt-1 text-sm text-text-secondary">
                Start screening smarter in minutes
              </p>
            </div>

            <div className="mb-4">
              <GoogleSignInButton onClick={() => { window.location.href = googleAuthUrl(); }} />
            </div>

            <div className="relative my-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-border-card" />
              </div>
              <div className="relative flex justify-center">
                <span className="bg-white px-3 text-xs text-text-secondary">or continue with email</span>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Full Name
                </label>
                <input
                  type="text"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Jane Smith"
                  required
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Work Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="jane@company.com"
                  required
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                />
              </div>

              <div>
                <label className="text-xs font-semibold uppercase tracking-[0.32px] text-text-secondary">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Minimum 8 characters"
                  minLength={8}
                  required
                  className="mt-1 w-full rounded-btn border border-border-card bg-white px-3 py-2.5 text-sm text-near-black focus:outline-none focus:shadow-focus"
                />
              </div>

              {error && (
                <p className="rounded-badge bg-rose-50 px-3 py-2 text-xs font-medium text-error-text">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-btn bg-rausch py-3 text-sm font-semibold text-white hover:bg-[#e0334f] disabled:opacity-50 transition-colors"
              >
                {loading ? "Creating account..." : "Create Account"}
              </button>
            </form>

            <p className="mt-6 text-center text-xs text-text-secondary">
              Already have an account?{" "}
              <Link to="/login" className="font-semibold text-near-black hover:text-rausch transition-colors">
                Sign in
              </Link>
            </p>

            <p className="mt-4 text-center text-[10px] text-text-secondary leading-relaxed">
              By creating an account you agree to our{" "}
              <a href="#" className="underline">Terms of Service</a>{" "}
              and{" "}
              <a href="#" className="underline">Privacy Policy</a>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
